import COLORS from '../../../constants/colors';

export const EVAL_STATUSES = {
  upcoming: 'upcoming',
  completed: 'completed',
};

export const CAPACITY_SLOT_STATUSES = {
  filled: 'filled',
  available: 'available',
};

export const MONTH_OPTION_COUNT = 18;
export const MONTHS_FORWARD = 3;

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

export const toMonthValue = date =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

export const currentMonthValue = () => toMonthValue(new Date());

export const formatMonthLabel = value => {
  const [year, month] = (value || '').split('-');
  const index = Number(month) - 1;

  return MONTH_LABELS[index] ? `${MONTH_LABELS[index]} ${year}` : value;
};

// Newest first: the months admins act on are the current one and the next few.
export const buildMonthOptions = () => {
  const now = new Date();
  const options = [];

  for (let offset = MONTHS_FORWARD; offset > -MONTH_OPTION_COUNT; offset -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const value = toMonthValue(date);

    options.push({ key: value, value, text: formatMonthLabel(value) });
  }

  return options;
};

const IST_OPTIONS = {
  timeZone: 'Asia/Kolkata',
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

export const formatIstDateTime = epochSeconds => {
  if (!epochSeconds && epochSeconds !== 0) return '-';

  return `${new Date(epochSeconds * 1000)
    .toLocaleString('en-IN', IST_OPTIONS)
    .replace(',', '')} IST`;
};

export const formatIstDate = value => {
  if (!value) return '-';
  const date = new Date(typeof value === 'number' ? value * 1000 : value);

  if (Number.isNaN(date.getTime())) return value;

  return date.toLocaleDateString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

// datetime-local is zone-less wall-clock, so pin IST rather than the admin's browser zone.
export const istLocalInputToEpoch = value => {
  if (!value) return null;
  const parsed = Date.parse(`${value}:00+05:30`);

  return Number.isNaN(parsed) ? null : Math.floor(parsed / 1000);
};

/**
 * Wall-clock "YYYY-MM-DDTHH:MM" read in an arbitrary zone, as epoch seconds.
 *
 * The zone's offset is measured *at that instant* rather than taken as a constant,
 * so a time either side of a daylight-saving change converts correctly. Done by
 * asking Intl what the wall clock is at a first guess and correcting by the drift.
 */
export const localInputToEpochInZone = (value, zone) => {
  if (!value) return null;
  if (!zone || zone === IST_ZONE) return istLocalInputToEpoch(value);

  const asUtc = Date.parse(`${value}:00Z`);

  if (Number.isNaN(asUtc)) return null;

  const wallClockAt = instant => {
    const parts = zoneFormatter(zone, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).formatToParts(new Date(instant));
    const get = type => (parts.find(part => part.type === type) || {}).value;

    return Date.parse(
      `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}:${get(
        'second',
      )}Z`,
    );
  };

  // One correction lands it; a second settles the rare case that crosses a transition.
  let guess = asUtc - (wallClockAt(asUtc) - asUtc);
  guess -= wallClockAt(guess) - asUtc;

  return Math.floor(guess / 1000);
};

export const formatIstTime = value => {
  if (!value) return '-';

  return new Date(value * 1000).toLocaleTimeString('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

// eval_scores is a per-dimension dict; rendering it directly crashes React.
export const describeEvalScore = scores => {
  if (!scores || typeof scores !== 'object') return '-';

  return scores.overall ?? '-';
};

export const describeInterval = interval => {
  if (!interval) return '-';

  return `${formatIstTime(interval.start)} – ${formatIstTime(interval.end)} IST`;
};

// GET hooks surface the raw {message} object, which React renders as nothing.
export const errorText = error => {
  if (!error) return null;
  if (typeof error === 'string') return error;

  return error.message || error.error?.message || 'Something went wrong.';
};

// Colour is the only thing separating a free hour from a booked one at a glance.
export const BLOCK_STATUSES = {
  available: {
    key: 'available',
    label: 'Available',
    color: COLORS.DARK_GREEN,
    background: 'rgba(0, 158, 96, 0.08)',
  },
  occupied: {
    key: 'occupied',
    label: 'Occupied',
    color: COLORS.RUSTY_RED,
    background: 'rgba(221, 51, 68, 0.08)',
  },
  paused: {
    key: 'paused',
    label: 'On leave / paused',
    color: COLORS.YELLOW_4,
    background: 'rgba(255, 183, 0, 0.10)',
  },
};

// "Tue 11 Aug" — the window rolls, so a bare weekday would be ambiguous.
export const formatDateColumn = date => {
  if (!date) return '-';

  const parsed = new Date(`${date}T00:00:00+05:30`);

  if (Number.isNaN(parsed.getTime())) return date;

  return parsed.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    timeZone: 'Asia/Kolkata',
  });
};

// Eval slots start on the hour or half hour, so 0-59 minutes offers invalid times.
export const ALL_HALF_HOURS = Array.from({ length: 48 }, (_, index) => {
  const hour = String(Math.floor(index / 2)).padStart(2, '0');

  return `${hour}:${index % 2 ? '30' : '00'}`;
});

export const HALF_HOUR_OPTIONS = ALL_HALF_HOURS.map(time => ({
  key: time,
  value: time,
  text: time,
}));

export const splitIstLocalInput = value => {
  const [date = '', time = ''] = (value || '').split('T');

  return { date, time: time.slice(0, 5) };
};

export const joinIstLocalInput = (date, time) => (date && time ? `${date}T${time}` : '');

// The server filters by state group; the tabs are just how it is offered.
export const MAPPING_STATE_GROUPS = { failed: 'failed', mapped: 'mapped' };

// ON_HOLD is only a reservation until the parent pays, so it stays distinct.
export const MAPPING_STATES = {
  ON_HOLD: { label: 'On hold', color: COLORS.YELLOW_4 },
  ASSIGNED: { label: 'Assigned', color: COLORS.DARK_GREEN },
  ACKNOWLEDGED: { label: 'Acknowledged', color: COLORS.DARK_GREEN },
  HOLD_EXPIRED: { label: 'Hold expired', color: COLORS.RUSTY_RED },
};

// For 3-5 a K-5 and a 3-8 coach both qualify; only the tier separates them.
export const TIER_GRADES = ['3', '4', '5'];
export const PLAN_TIERS = ['Basic', 'Standard'];

export const gradeNeedsTier = grade => TIER_GRADES.includes(String(grade ?? '').trim());

export const PLAN_TIER_OPTIONS = PLAN_TIERS.map(value => ({
  key: value,
  value,
  text: value,
}));

// A demo stores slot epochs in seconds or milliseconds depending on source.
export const epochToIstLocalInput = value => {
  if (!value) return '';

  const seconds =
    Number(value) >= 1e12 ? Math.floor(Number(value) / 1000) : Number(value);
  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Kolkata',
  })
    .formatToParts(new Date(seconds * 1000))
    .reduce((acc, part) => ({ ...acc, [part.type]: part.value }), {});

  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
};

// Always two rows: the eval flow books a pair, so a missing one must still render.
export const epochsToIstInputs = (values, count = 2) =>
  Array.from({ length: count }, (_, index) =>
    epochToIstLocalInput((values || [])[index]),
  );

// Blocks carry raw epochs, so the zone is only how they are rendered.
export const IST_ZONE = 'Asia/Kolkata';
const IST_OPTION = { value: IST_ZONE, text: 'IST — India' };

// The zones a region's parents actually sit in. IST is prepended to every list
// rather than repeated here: the coaches read these screens from India.
const REGION_ZONES = {
  NAM: [
    { value: 'America/New_York', text: 'US Eastern' },
    { value: 'America/Chicago', text: 'US Central' },
    { value: 'America/Denver', text: 'US Mountain' },
    { value: 'America/Phoenix', text: 'US Arizona' },
    { value: 'America/Los_Angeles', text: 'US Pacific' },
    { value: 'America/Anchorage', text: 'US Alaska' },
    { value: 'Pacific/Honolulu', text: 'US Hawaii' },
    { value: 'America/Toronto', text: 'Canada Eastern' },
    { value: 'America/Vancouver', text: 'Canada Pacific' },
    { value: 'America/Sao_Paulo', text: 'Brazil — São Paulo' },
    { value: 'America/Mexico_City', text: 'Mexico City' },
  ],
  APAC: [
    { value: 'Australia/Sydney', text: 'Sydney' },
    { value: 'Australia/Melbourne', text: 'Melbourne' },
    { value: 'Australia/Brisbane', text: 'Brisbane' },
    { value: 'Australia/Adelaide', text: 'Adelaide' },
    { value: 'Australia/Perth', text: 'Perth' },
    { value: 'Pacific/Auckland', text: 'New Zealand' },
    { value: 'Asia/Singapore', text: 'Singapore' },
    { value: 'Asia/Hong_Kong', text: 'Hong Kong' },
    { value: 'Asia/Tokyo', text: 'Japan' },
  ],
  EUK: [
    { value: 'Europe/London', text: 'UK' },
    { value: 'Europe/Dublin', text: 'Ireland' },
    { value: 'Europe/Paris', text: 'Central Europe — Paris' },
    { value: 'Europe/Berlin', text: 'Central Europe — Berlin' },
    { value: 'Europe/Madrid', text: 'Spain' },
    { value: 'Europe/Athens', text: 'Eastern Europe — Athens' },
  ],
  IME: [
    { value: 'Asia/Dubai', text: 'Dubai — UAE' },
    { value: 'Asia/Riyadh', text: 'Saudi Arabia' },
    { value: 'Asia/Qatar', text: 'Qatar' },
    { value: 'Asia/Kuwait', text: 'Kuwait' },
    { value: 'Asia/Bahrain', text: 'Bahrain' },
    { value: 'Asia/Muscat', text: 'Oman' },
  ],
};

const withKeys = options =>
  options.map(({ value, text }) => ({ key: value, value, text }));

// Every region reads in IST first, then its own zones.
export const timezoneOptionsForRegion = region =>
  withKeys(
    region && REGION_ZONES[region]
      ? [IST_OPTION, ...REGION_ZONES[region]]
      : [IST_OPTION, ...Object.values(REGION_ZONES).flat()],
  );

export const TIMEZONE_OPTIONS = timezoneOptionsForRegion();

/** Same shape as formatIstDateTime, read in the zone the screen is showing. */
export const formatDateTimeInZone = (epochSeconds, zone) => {
  if (!epochSeconds && epochSeconds !== 0) return '-';
  if (!zone || zone === IST_ZONE) return formatIstDateTime(epochSeconds);

  const label =
    (TIMEZONE_OPTIONS.find(option => option.value === zone) || {}).text || zone;

  return `${new Date(epochSeconds * 1000)
    .toLocaleString('en-GB', { ...IST_OPTIONS, timeZone: zone })
    .replace(',', '')} ${label}`;
};

const zoneFormatter = (zone, options) =>
  new Intl.DateTimeFormat('en-GB', { ...options, timeZone: zone });

export const formatEpochTimeInZone = (epoch, zone) =>
  epoch
    ? zoneFormatter(zone, { hour: '2-digit', minute: '2-digit', hour12: false }).format(
        new Date(Number(epoch) * 1000),
      )
    : '-';

// An IST date can land on a different day elsewhere, so recompute per zone.
export const epochDateKeyInZone = (epoch, zone) =>
  epoch
    ? zoneFormatter(zone, { year: 'numeric', month: '2-digit', day: '2-digit' })
        .format(new Date(Number(epoch) * 1000))
        .split('/')
        .reverse()
        .join('-')
    : '';

export const formatDateColumnInZone = (dateKey, zone) => {
  if (!dateKey) return '-';

  const parsed = new Date(`${dateKey}T12:00:00Z`);

  return Number.isNaN(parsed.getTime())
    ? dateKey
    : zoneFormatter(zone, {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
      }).format(parsed);
};

// Today in IST as a date input value, so neither a slot nor a pause can be set
// in the past — a past slot can never be booked and a past pause protects nothing.
export const istTodayInputDate = () =>
  new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: IST_ZONE,
  }).format(new Date());

export const isPastIstInput = value => {
  if (!value) return false;

  const epoch = istLocalInputToEpoch(value);

  return Boolean(epoch) && epoch * 1000 < Date.now();
};

// "Region" means two different things: a coach holds a licence region, a parent sits in
// an opportunity region. Teacher-subject screens use the first, parent/student the second.
export const ALL_REGIONS_VALUE = 'all';

export const LICENCE_REGIONS = ['NAM', 'APAC', 'EUK', 'IME'];

export const OPPORTUNITY_REGIONS = [
  { value: 'USA', text: 'USA' },
  { value: 'AUS', text: 'Australia' },
  { value: 'ASIA', text: 'Asia' },
  { value: 'MIDDLE-EAST', text: 'Middle East' },
  { value: 'IND', text: 'India' },
  { value: 'EUROPE', text: 'Europe' },
];

const allRegionsOption = {
  key: ALL_REGIONS_VALUE,
  value: ALL_REGIONS_VALUE,
  text: 'All regions',
};

export const LICENCE_REGION_OPTIONS = [
  allRegionsOption,
  ...LICENCE_REGIONS.map(value => ({ key: value, value, text: value })),
];

export const OPPORTUNITY_REGION_OPTIONS = [
  allRegionsOption,
  ...OPPORTUNITY_REGIONS.map(({ value, text }) => ({ key: value, value, text })),
];

// Presets cover the two views people actually ask for; anything else is a custom range.
export const RANGE_TYPES = { week: 'week', month: 'month', custom: 'custom' };

export const RANGE_OPTIONS = [
  { key: RANGE_TYPES.week, value: RANGE_TYPES.week, text: 'Next 7 days' },
  { key: RANGE_TYPES.month, value: RANGE_TYPES.month, text: 'This month' },
  { key: RANGE_TYPES.custom, value: RANGE_TYPES.custom, text: 'Date range' },
];

// Mirrors MAX_RANGE_DAYS in the gateway, so an over-long range is caught before the request.
export const MAX_RANGE_DAYS = 31;

export const addDaysToInputDate = (date, days) => {
  const parsed = new Date(`${date}T00:00:00Z`);

  if (Number.isNaN(parsed.getTime())) return '';
  parsed.setUTCDate(parsed.getUTCDate() + days);

  return parsed.toISOString().slice(0, 10);
};

export const daysBetweenInputDates = (start, end) => {
  const from = new Date(`${start}T00:00:00Z`);
  const to = new Date(`${end}T00:00:00Z`);

  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return null;

  return Math.round((to - from) / 86400000) + 1;
};

/**
 * Validation message for a custom range, or null when it is fine to send.
 *
 * With time included the range is a pair of instants, so only the ordering matters:
 * the day cap exists to stop the availability template being projected across months,
 * which a minute-precision window on an audit timestamp does not do.
 */
export const rangeError = (start, end, withTime = false) => {
  const missing = withTime
    ? 'Pick both a start and an end.'
    : 'Pick both a start and an end date.';

  if (!start || !end) return missing;

  if (withTime) {
    const from = Date.parse(`${start}:00`);
    const to = Date.parse(`${end}:00`);

    if (Number.isNaN(from) || Number.isNaN(to)) return missing;

    return to < from ? 'The end cannot be before the start.' : null;
  }

  const span = daysBetweenInputDates(start, end);

  if (span === null) return missing;
  if (span < 1) return 'End date cannot be before the start date.';
  if (span > MAX_RANGE_DAYS) return `Pick a range of ${MAX_RANGE_DAYS} days or fewer.`;

  return null;
};

/**
 * Query args for the window, so both screens send the same shape.
 *
 * The zone travels with the dates: "12 Aug" has to mean the viewer's midnight, not
 * IST's, or a US range is off by the whole offset at both edges. The backend does the
 * boundary maths from the zone name so a range spanning a DST change gets the right
 * offset at each end.
 */
export const rangeQuery = ({ rangeType, month, startDate, endDate }, zone) => {
  const tz = zone && zone !== IST_ZONE ? { tz: zone } : {};

  if (rangeType === RANGE_TYPES.custom) {
    // The dates name whole days, and `tz` says whose days they are; the service turns
    // that pair into the epoch bounds it filters on.
    return {
      range: RANGE_TYPES.custom,
      start_date: startDate,
      end_date: endDate,
      ...tz,
    };
  }
  if (rangeType === RANGE_TYPES.month) return { range: RANGE_TYPES.month, month, ...tz };

  return { range: RANGE_TYPES.week, ...tz };
};
